import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Calculator, Wifi, ArrowRight } from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/internet-calculator")({
  head: () => {
    const url = `${SITE_URL}/internet-calculator`;
    const title =
      "Internet Snelheid Calculator — Bereken benodigde Mbps | Elvion Group Nederland";
    const description =
      "Bereken welke internetsnelheid (Mbps) je nodig hebt op basis van gebruikers, apparaten en activiteiten zoals 4K streaming, gaming en videobellen.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: InternetCalculatorPage,
});

// Bandbreedte in Mbps (download) per activiteit, per gelijktijdige stream
type Activity = {
  key: string;
  label: string;
  mbps: number;
  hint: string;
};

const ACTIVITIES: Activity[] = [
  { key: "browsing", label: "Browsen & e-mail", mbps: 3, hint: "Websites, social, mail" },
  { key: "sd", label: "SD video streamen", mbps: 3, hint: "YouTube 480p" },
  { key: "hd", label: "HD video (1080p)", mbps: 8, hint: "Netflix, YouTube HD" },
  { key: "uhd", label: "4K / UHD streaming", mbps: 25, hint: "Netflix/Disney+ 4K" },
  { key: "videocall", label: "Videobellen (HD)", mbps: 5, hint: "Teams, Zoom, Meet" },
  { key: "videocall4k", label: "Videobellen (4K)", mbps: 15, hint: "Zakelijk hoge kwaliteit" },
  { key: "gaming", label: "Online gaming", mbps: 10, hint: "PlayStation/Xbox/PC" },
  { key: "cloudgaming", label: "Cloud gaming", mbps: 35, hint: "GeForce Now, xCloud" },
  { key: "download", label: "Grote downloads", mbps: 50, hint: "Games, updates" },
  { key: "smarthome", label: "Smart home / IoT", mbps: 2, hint: "Camera's, sensoren" },
  { key: "cctv", label: "IP-camera (4K)", mbps: 16, hint: "Per camera, upload" },
  { key: "werken", label: "Thuiswerken (cloud)", mbps: 10, hint: "VPN, SaaS, bestanden" },
];

const CONNECTION_TIERS = [
  { mbps: 50, label: "50 Mbps", type: "Basis (DSL/4G)" },
  { mbps: 100, label: "100 Mbps", type: "Kabel / instap glasvezel" },
  { mbps: 250, label: "250 Mbps", type: "Glasvezel standaard" },
  { mbps: 500, label: "500 Mbps", type: "Glasvezel ruim" },
  { mbps: 1000, label: "1 Gbps", type: "Glasvezel premium" },
  { mbps: 2000, label: "2 Gbps", type: "Glasvezel zakelijk" },
];

const FAQS = [
  {
    q: "Hoe wordt de benodigde internetsnelheid berekend?",
    a: "Wij tellen de bandbreedte van alle gekozen activiteiten op (Mbps per stream), vermenigvuldigen met het aantal gelijktijdige gebruikers en tellen daar een buffer van 20% bovenop voor pieken, overhead en achtergrondverkeer (updates, cloud-sync, smart home).",
  },
  {
    q: "Wat is het verschil tussen Mbps en MB/s?",
    a: "Mbps staat voor Megabit per seconde (netwerksnelheid). MB/s is Megabyte per seconde (downloadsnelheid). 1 byte = 8 bits, dus 100 Mbps ≈ 12,5 MB/s. Providers adverteren altijd in Mbps, downloadmanagers tonen meestal MB/s.",
  },
  {
    q: "Hoeveel Mbps heb ik nodig voor 4K streaming?",
    a: "Netflix en Disney+ adviseren minimaal 25 Mbps per 4K-stream. Voor twee gelijktijdige 4K-streams is dus minstens 50 Mbps nodig, plus ruimte voor andere apparaten in huis.",
  },
  {
    q: "Is glasvezel echt sneller dan kabel of DSL?",
    a: "Ja. Glasvezel levert symmetrische snelheden (gelijke up- en download) tot 10 Gbps en heeft een veel lagere latency (ping) dan kabel of DSL. Vooral voor gaming, videobellen en thuiswerken merk je het verschil direct.",
  },
  {
    q: "Waarom is upload-snelheid belangrijk?",
    a: "Upload bepaalt hoe snel je bestanden naar de cloud stuurt, hoe stabiel je videogesprek is en hoe vloeiend IP-camera's beelden doorgeven. Voor thuiswerken en cameratoezicht adviseren wij minstens 50 Mbps upload — alleen glasvezel biedt dit standaard symmetrisch.",
  },
  {
    q: "Wat is een goede ping/latency?",
    a: "Voor browsen en streaming maakt ping nauwelijks uit. Voor online gaming wil je <30 ms, voor competitief gaming <15 ms. Glasvezel zit doorgaans op 2–10 ms, kabel op 10–25 ms, 4G op 30–60 ms en satelliet (Starlink) op 25–50 ms.",
  },
  {
    q: "Hoeveel apparaten kan mijn router aan?",
    a: "Een moderne Wi-Fi 6 router kan 50–100 apparaten tegelijk bedienen. Een Wi-Fi 5 router komt in de praktijk tot 25–40 actieve apparaten. Voor grotere huizen of kantoren met veel IoT adviseren wij een mesh-systeem met aparte access points.",
  },
  {
    q: "Heb ik wifi 6 of 6E nodig?",
    a: "Wi-Fi 6 (802.11ax) is de huidige standaard en presteert duidelijk beter bij veel apparaten tegelijk. Wi-Fi 6E voegt de 6 GHz-band toe — meer kanalen, minder storing, maar alleen nuttig als je apparaten 6E ondersteunen en je dichtbij het access point zit.",
  },
  {
    q: "Bekabeld of wifi voor gaming en streaming?",
    a: "Voor 4K-streaming, gaming-pc/console en NAS adviseren wij altijd bekabeld (CAT6/CAT6a). Bekabeld is sneller, stabieler en met lagere latency dan zelfs het beste wifi-netwerk. Wifi gebruik je voor mobiele apparaten en IoT.",
  },
  {
    q: "Welke snelheid is genoeg voor een gemiddeld gezin?",
    a: "Een gezin van 4 met streaming, gaming, thuiswerken en smart home zit comfortabel op 250–500 Mbps. Met meerdere 4K-streams en cloud gaming is 1 Gbps glasvezel de veilige keuze.",
  },
];

function InternetCalculatorPage() {
  const [users, setUsers] = useState(3);
  const [selected, setSelected] = useState<Record<string, number>>({
    hd: 1,
    videocall: 1,
    browsing: 2,
    smarthome: 1,
  });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const { totalMbps, advisedMbps, breakdown, recommendedTier } = useMemo(() => {
    const breakdown = ACTIVITIES.filter((a) => (selected[a.key] ?? 0) > 0).map(
      (a) => ({
        ...a,
        count: selected[a.key],
        subtotal: a.mbps * (selected[a.key] ?? 0),
      }),
    );
    const baseMbps = breakdown.reduce((s, b) => s + b.subtotal, 0);
    // Gelijktijdigheidsfactor: niet iedereen doet alles tegelijk
    const concurrency = Math.min(1, 0.5 + users * 0.1);
    const totalMbps = Math.round(baseMbps * concurrency);
    // 20% buffer voor pieken/overhead
    const advisedMbps = Math.round(totalMbps * 1.2);
    const recommendedTier =
      CONNECTION_TIERS.find((t) => t.mbps >= advisedMbps) ??
      CONNECTION_TIERS[CONNECTION_TIERS.length - 1];
    return { totalMbps, advisedMbps, breakdown, recommendedTier };
  }, [selected, users]);

  const setCount = (key: string, n: number) =>
    setSelected((s) => ({ ...s, [key]: Math.max(0, n) }));

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Internet Snelheid Calculator: bereken hoeveel Mbps je nodig hebt
        </h1>
        <p className="mt-4 max-w-3xl text-base text-text-secondary sm:text-lg">
          Welke <strong>internetsnelheid</strong> past bij jouw huishouden of
          kantoor? Vul het aantal gebruikers in en kies welke activiteiten je
          gelijktijdig doet. Wij berekenen direct de benodigde bandbreedte in
          Mbps en adviseren een passende verbinding (DSL, kabel of glasvezel).
        </p>

        {/* Calculator */}
        <section className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="mb-6 flex items-center gap-2 text-orange">
              <Calculator className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Calculator
              </span>
            </div>

            <div className="space-y-6">
              <div>
                <label className="flex items-center justify-between text-sm font-semibold text-foreground">
                  Aantal gebruikers
                  <span className="rounded-md bg-muted px-2 py-0.5 text-base font-bold text-foreground">
                    {users}
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={users}
                  onChange={(e) => setUsers(Number(e.target.value))}
                  className="mt-3 w-full accent-orange"
                />
                <div className="mt-1 flex justify-between text-xs text-text-secondary">
                  <span>1 persoon</span>
                  <span>gezin 4</span>
                  <span>20 personen</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-foreground">
                  Activiteiten (aantal gelijktijdige streams)
                </label>
                <div className="mt-3 space-y-2">
                  {ACTIVITIES.map((a) => {
                    const count = selected[a.key] ?? 0;
                    const active = count > 0;
                    return (
                      <div
                        key={a.key}
                        className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors ${
                          active
                            ? "border-orange bg-orange/5"
                            : "border-border bg-background"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">
                            {a.label}
                            <span className="ml-2 text-xs font-normal text-text-secondary">
                              {a.mbps} Mbps/stream
                            </span>
                          </p>
                          <p className="truncate text-xs text-text-secondary">
                            {a.hint}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setCount(a.key, count - 1)}
                            className="size-7 rounded-md border border-border text-foreground hover:border-orange hover:text-orange"
                            aria-label={`Minder ${a.label}`}
                          >
                            −
                          </button>
                          <span className="w-6 text-center text-sm font-bold">
                            {count}
                          </span>
                          <button
                            type="button"
                            onClick={() => setCount(a.key, count + 1)}
                            className="size-7 rounded-md border border-border text-foreground hover:border-orange hover:text-orange"
                            aria-label={`Meer ${a.label}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-text-secondary">
                  Tip: voeg apparaten toe die op piekmomenten écht tegelijk
                  actief zijn (bv. 2× Netflix + 1× videobellen + smart home).
                </p>
              </div>
            </div>
          </div>

          {/* Resultaat */}
          <div className="rounded-2xl border border-orange/30 bg-gradient-to-br from-orange/5 to-transparent p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center gap-2 text-orange">
              <Wifi className="size-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Jouw internetadvies
              </span>
            </div>

            <p className="text-sm text-text-secondary">Geadviseerde snelheid</p>
            <p className="mt-1 text-4xl font-bold tracking-tight text-foreground">
              {advisedMbps} Mbps
            </p>
            <p className="mt-1 text-xs text-text-secondary">
              Berekend gebruik {totalMbps} Mbps · incl. 20% buffer voor pieken
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Aanbevolen abonnement
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {recommendedTier.label}
                </p>
                <p className="text-xs text-text-secondary">
                  {recommendedTier.type}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Per gebruiker
                </p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  ± {Math.round(advisedMbps / Math.max(1, users))} Mbps
                </p>
                <p className="text-xs text-text-secondary">
                  bij {users} gebruiker{users === 1 ? "" : "s"}
                </p>
              </div>
            </div>

            {breakdown.length > 0 && (
              <div className="mt-6">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                  Verdeling per activiteit
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {breakdown.map((b) => (
                    <li
                      key={b.key}
                      className="flex justify-between border-b border-border/60 py-1 text-text-secondary last:border-0"
                    >
                      <span>
                        {b.label}{" "}
                        <span className="text-xs">× {b.count}</span>
                      </span>
                      <span className="font-semibold text-foreground">
                        {b.subtotal} Mbps
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/c/$category"
                params={{ category: "netwerken" }}
                className="inline-flex items-center gap-2 rounded-md bg-orange px-4 py-2 text-sm font-semibold text-white hover:bg-orange/90"
              >
                Naar netwerkproducten
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/"
                hash="contact"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                Netwerkadvies aanvragen
              </Link>
            </div>
          </div>
        </section>

        {/* Verbindingstabel */}
        <section className="mt-12">
          <h2 className="text-xl font-bold tracking-tight">
            Welke verbinding past bij welke snelheid?
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Richtlijn op basis van Nederlandse providers (KPN, Ziggo, Odido,
            Delta).
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className="px-4 py-3">Snelheid</th>
                  <th className="px-4 py-3">Type verbinding</th>
                  <th className="px-4 py-3">Geschikt voor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  {
                    s: "50 Mbps",
                    t: "DSL / 4G",
                    g: "1–2 gebruikers, browsen + 1× HD streaming",
                  },
                  {
                    s: "100 Mbps",
                    t: "Kabel / instap glasvezel",
                    g: "Gezin met HD streaming + videobellen",
                  },
                  {
                    s: "250 Mbps",
                    t: "Glasvezel standaard",
                    g: "Gezin met 4K streaming + gaming + thuiswerken",
                  },
                  {
                    s: "500 Mbps",
                    t: "Glasvezel ruim",
                    g: "Meerdere 4K-streams + cloud gaming + IP-camera's",
                  },
                  {
                    s: "1 Gbps",
                    t: "Glasvezel premium",
                    g: "Groot gezin, smart home, NAS, frequente uploads",
                  },
                  {
                    s: "2 Gbps+",
                    t: "Glasvezel zakelijk",
                    g: "Kantoor, studio, veel gelijktijdige videogesprekken",
                  },
                ].map((r) => (
                  <tr
                    key={r.s}
                    className={
                      r.s === recommendedTier.label ? "bg-orange/5" : ""
                    }
                  >
                    <td className="px-4 py-3 font-semibold text-foreground">
                      {r.s}
                    </td>
                    <td className="px-4 py-3">{r.t}</td>
                    <td className="px-4 py-3 text-text-secondary">{r.g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Tips */}
        <section className="mt-12 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Waarom glasvezel?
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Symmetrische snelheden — download = upload",
                "Latency van 2–10 ms, ideaal voor gaming en videobellen",
                "Stabiel onder piekbelasting, geen vertraging in de avonduren",
                "Toekomstvast: tot 10 Gbps op dezelfde vezel",
                "Lagere CO₂-footprint dan kabel of koper",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Tips voor een snel netwerk
            </h2>
            <ul className="mt-3 space-y-2">
              {[
                "Gebruik CAT6 of CAT6a bekabeling tussen router en access points",
                "Plaats je router centraal en vrij — niet in een meterkast achter staal",
                "Kies Wi-Fi 6 of 6E voor huishoudens met 10+ apparaten",
                "Gebruik een mesh-systeem in huizen >100 m² of meerdere verdiepingen",
                "Sluit gaming-pc, console en NAS altijd bekabeld aan",
              ].map((b) => (
                <li
                  key={b}
                  className="flex gap-2 text-text-secondary before:mt-2 before:size-1.5 before:shrink-0 before:rounded-full before:bg-orange"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold tracking-tight">
            Veelgestelde vragen over internetsnelheid
          </h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border bg-card">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className="p-5">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="flex w-full items-center justify-between text-left font-semibold text-foreground"
                  >
                    {f.q}
                    <span
                      className={`ml-3 text-orange transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </button>
                  {isOpen && <p className="mt-2 text-text-secondary">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-card p-6 ring-1 ring-border sm:p-8">
          <h2 className="text-xl font-bold tracking-tight">
            Hulp nodig bij je netwerkinstallatie?
          </h2>
          <p className="mt-2 text-text-secondary">
            Onze installateurs leggen glasvezel, CAT6/CAT6a-bekabeling en mesh
            wifi aan voor woningen en kantoren — inclusief configuratie van
            router, switches en access points.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/c/$category"
              params={{ category: "netwerken" }}
              className="inline-flex items-center rounded-md bg-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange/90"
            >
              Bekijk netwerkproducten
            </Link>
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
            >
              Neem contact op
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
